import * as React from 'react';

import debounce from 'lodash/debounce';

import {ListViewInfiniteLoading} from './list-view-infinite-loading';

import styles from './list-view-paginated.module.css';

type Props = {
    isLoading: boolean;
    hasNextPage: boolean;
    onFetchMoreRows: () => any;
    renderBody: any;
    // Element id of scroll container for the list, if undefined, the default scroll container will be used
    scrollContainerId?: string;
};

// This component will render anything passed through renderBody and append a loading indicator and scroll listener onto it. The content to be rendered is left up to the caller.
// If you want to render a list consider using the ListViewPaginated component where the caller is only responsible for appending rows.

export function PaginatedView(props: Props) {
    const {hasNextPage, isLoading, onFetchMoreRows, scrollContainerId} = props;
    const [shouldFetchMore, setShouldFetchMore] = React.useState<boolean>(false);

    const containerRef = React.useRef();

    /**
     * Effect handles fetching more when shouldFetchMore state is triggered
     */
    React.useEffect(() => {
        if (shouldFetchMore) {
            onFetchMoreRows().then(() => {
                setShouldFetchMore(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldFetchMore]);

    /**
     * Effect to handle onScroll listener to fetch more rows
     */
    React.useEffect(() => {
        // Define scroll handler, which uses debounce to reduce number of times it runs
        const handleScroll = debounce(() => {
            if (hasNextPage && !isLoading) {
                setShouldFetchMore(true);
            }
        }, 100);

        let scrollContainerElement = containerRef && containerRef.current;

        if (scrollContainerId) {
            const scrollContainer = document.getElementById(scrollContainerId);

            if (scrollContainer) {
                // @ts-expect-error
                scrollContainerElement = scrollContainer;
            }
        }

        if (scrollContainerElement) {
            // @ts-expect-error
            scrollContainerElement.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (scrollContainerElement) {
                // @ts-expect-error
                scrollContainerElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, [hasNextPage, isLoading, scrollContainerId]);

    return (
        <div
            // @ts-expect-error
            ref={containerRef}
            className={scrollContainerId ? undefined : styles['list-view-paginated']}
        >
            {props.renderBody}
            {isLoading || hasNextPage ? <ListViewInfiniteLoading /> : undefined}
        </div>
    );
}
